import { useState, useEffect, useCallback } from "react";

const DISMISS_KEY = "mfc-install-dismissed";

function detect() {
  const ua = navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(ua);
  // iOS Safari (not Chrome/Firefox iOS which can't install)
  const iosOtherBrowser = isIOS && /crios|fxios|edgios|opt\//i.test(ua);
  const standalone = window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  return { isIOS, isAndroid, iosOtherBrowser, standalone };
}

export function usePWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [env, setEnv] = useState(detect);
  const [installed, setInstalled] = useState(detect().standalone);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    const onDisplay = () => setInstalled(mq.matches || window.navigator.standalone === true);
    mq.addEventListener?.("change", onDisplay);
    setEnv(detect());
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      mq.removeEventListener?.("change", onDisplay);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") setDeferred(null);
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }, []);

  // platform: "native" (browser supports install prompt) | "ios" | "ios-other" | "android" | "desktop"
  let platform = "desktop";
  if (deferred) platform = "native";
  else if (env.isIOS) platform = env.iosOtherBrowser ? "ios-other" : "ios";
  else if (env.isAndroid) platform = "android";

  return {
    canInstall: !!deferred,
    installed,
    dismissed,
    platform,
    env,
    promptInstall,
    dismiss,
    // eligible to surface an install affordance at all
    eligible: !installed && (!!deferred || env.isIOS || env.isAndroid),
  };
}
